import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { supabase } from "../../supabaseClient";

export default function WorkshopBookingSuccess() {
  const [params] = useSearchParams();
  const bookingId = params.get("bookingId");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [booking, setBooking] = useState(null);

  const tryFetch = async () => {
    if (!bookingId) {
      setError("Missing bookingId");
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      // Attempt to include related workshop details if available
      const { data, error: err } = await supabase
        .from("bookings")
        .select(
          "id,type,status,total_amount_cents,currency,created_at,workshops:workshop_id(title,start_at)"
        )
        .eq("id", bookingId)
        .single();
      if (err) throw err;
      setBooking(data || null);
      setError(null);
    } catch (e) {
      console.warn("[WorkshopBookingSuccess] fetch error", e);
      setError("Could not load booking");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let mounted = true;
    const run = async () => {
      await tryFetch();
      // Light polling for webhook update (up to ~20s)
      const start = Date.now();
      const poll = async () => {
        if (!mounted) return;
        if (Date.now() - start > 20000) return; // stop after ~20s
        const { data } = await supabase
          .from("bookings")
          .select("status,total_amount_cents,currency,workshops:workshop_id(title,start_at)")
          .eq("id", bookingId)
          .single();
        if (!mounted) return;
        if (data) setBooking((prev) => ({ ...(prev || {}), ...data }));
        if (data?.status === "paid" || data?.status === "confirmed") return; // stop when paid
        setTimeout(poll, 2500);
      };
      setTimeout(poll, 2500);
    };
    run();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId]);

  const isConfirmed = booking?.status === "paid" || booking?.status === "confirmed";
  const title = booking?.workshops?.title || null;
  const when = booking?.workshops?.start_at ? new Date(booking.workshops.start_at) : null;

  return (
    <div className="min-h-[70vh] flex items-center justify-center text-[var(--mudo-text)]">
      <div className="p-6 rounded-xl bg-white/5 border border-white/10 text-center max-w-lg mx-4">
        {loading ? (
          <div>
            <div className="w-16 h-16 border-4 border-amber-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <div className="text-lg font-semibold">Checking your booking…</div>
            <div className="text-sm opacity-80 mt-1">This may take a few seconds.</div>
          </div>
        ) : error ? (
          <div>
            <div className="text-lg font-semibold mb-1">We couldn’t find this booking.</div>
            <div className="text-sm opacity-80 mb-4">Please contact the studio if you need help.</div>
            <div className="mt-4">
              <Link
                to="/discover/workshops"
                className="px-4 py-2 rounded bg-amber-600 hover:bg-amber-700 text-white"
              >
                Back to workshops
              </Link>
            </div>
          </div>
        ) : booking ? (
          <div>
            {isConfirmed ? (
              <>
                <div className="text-xl font-semibold mb-2">Booking confirmed</div>
                {title ? (
                  <div className="text-sm opacity-90 mb-2">{title}{when ? ` • ${when.toLocaleString()}` : ""}</div>
                ) : null}
                <div className="text-sm opacity-80">You’ll receive a confirmation shortly.</div>
                <div className="mt-5">
                  <Link
                    to="/discover/workshops"
                    className="px-4 py-2 rounded bg-amber-600 hover:bg-amber-700 text-white"
                  >
                    Browse more workshops
                  </Link>
                </div>
              </>
            ) : (
              <>
                <div className="text-xl font-semibold mb-2">Payment received</div>
                <div className="text-sm opacity-80">
                  We’re still finalising your booking — please refresh or check back shortly.
                </div>
                <div className="mt-5 flex items-center justify-center gap-3">
                  <button
                    className="px-4 py-2 rounded border border-white/15 text-white/90"
                    onClick={() => tryFetch()}
                  >
                    Refresh now
                  </button>
                  <Link
                    to="/discover/workshops"
                    className="px-4 py-2 rounded bg-amber-600 hover:bg-amber-700 text-white"
                  >
                    Back to workshops
                  </Link>
                </div>
              </>
            )}
          </div>
        ) : (
          <div>
            <div className="text-lg font-semibold mb-1">We couldn’t find this booking.</div>
            <div className="text-sm opacity-80 mb-4">Please contact the studio if you need help.</div>
            <div className="mt-4">
              <Link
                to="/discover/workshops"
                className="px-4 py-2 rounded bg-amber-600 hover:bg-amber-700 text-white"
              >
                Back to workshops
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
